<template>
  <section v-if="ready" class="profile-question content-with-spacing">
    <k-panel
      class="question-container"
      title="Knowledge Base"
      icon="fa-comments"
      :duotone="false"
      :has-content-toggle="false"
    >
      <template #body>
        <div class="k-table-main-container">
          <k-table
            :rows="tableRows"
            :headers="tableHeaders"
            :panel="false"
            :hideable="false"
            :show-controls="false"
            empty-message="You have not posted any questions yet"
          />
          <pagination
            v-if="numOfPages"
            :model-value="currentPage"
            :count="numOfPages"
            :total-entries="threadsTotal"
            :displayed-entries="threads.length"
            @update:modelValue="onPageChange"
          />
        </div>
      </template>
    </k-panel>
  </section>
</template>

<style scoped>
.k-table-main-container {
  overflow-x: auto;
}

.k-table-main-container :deep(.k-table-container .k-table-header-status),
.k-table-main-container :deep(.k-table-container .k-table-key-status),
.k-table-main-container :deep(.k-table-container .k-table-header-visibility),
.k-table-main-container :deep(.k-table-container .k-table-key-visibility),
.k-table-main-container :deep(.k-table-container .k-table-header-upvotes),
.k-table-main-container :deep(.k-table-container .k-table-key-upvotes),
.k-table-main-container :deep(.k-table-container .k-table-header-answers),
.k-table-main-container :deep(.k-table-container .k-table-key-answers) {
  text-align: center;
}

.panel-pagination.row {
  display: flex;
  justify-content: space-between;
  margin: 0;
  align-items: center;
  padding-bottom: 15px;
  padding-top: 15px;
  width: 85%;
}
</style>

<script>
import useGlobalStore from '@stores/global';
import TimeMixin from '@mixins/time-mixins';
import PageReadyMixin from '@mixins/page-ready-mixin';
import Pagination from '@base-components/k-pagination.vue';
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';

export default {
  components: {
    pagination: Pagination,
    'k-panel': KPanel,
    'k-table': KTable,
  },

  mixins: [TimeMixin, PageReadyMixin],

  data() {
    return {
      store: useGlobalStore(),
      threads: [],
      threadsTotal: 0,
      threadsReady: false,
      currentPage: 0,
      maxItemsPerPage: 10,
    };
  },

  beforeMount() {
    this.getUserQuestions(false);
    this.$Loading.start();
  },

  computed: {
    ready() {
      return this.threadsReady;
    },
    studentId() {
      return this.store.userId;
    },
    numOfPages() {
      if (this.threadsTotal <= this.maxItemsPerPage) {
        return undefined;
      }
      return Math.ceil(this.threadsTotal / this.maxItemsPerPage);
    },
    tableHeaders() {
      return {
        question: {
          type: 'url',
          name: 'Questions Posted',
        },
        module: {
          name: 'Module',
        },
        status: {
          name: 'Status',
          type: 'pill',
          options: {
            type: statusValue => (statusValue === 'Answered' ? 'success' : 'empty'),
          },
        },
        visibility: {
          name: 'Visibility',
          type: 'pill',
          options: {
            type: statusValue => (statusValue === 'Private' ? 'warning' : 'primary'),
          },
        },
        upvotes: {
          name: 'Upvotes',
          type: 'string',
        },
        answers: {
          name: 'Answers',
          type: 'string',
        },
      };
    },
    tableRows() {
      return this.threads.map(thread => ({
        id: thread.id,
        question: {
          text: thread.title,
          path: this.linkToThread(thread),
        },
        module: thread.module_name || '',
        status: this.isAnswered(thread) ? 'Answered' : '-',
        visibility: this.isPrivate(thread) ? 'Private' : 'Public',
        upvotes: String(thread.votes ?? 0),
        answers: String(thread.response_count ?? 0),
      }));
    },
  },

  methods: {
    onPageChange(pageIndex) {
      this.currentPage = pageIndex;
      this.$Loading.minimal();
      this.getUserQuestions(true);
    },
    isAnswered(thread) {
      return thread.accepted_id !== null;
    },
    isPrivate(thread) {
      return thread.is_private === true;
    },
    getAssetId(thread) {
      return thread.pak_id || thread.quiz_id || thread.learning_unit_id || thread.video_id || thread.slide_deck_id || thread.calendar_event_id;
    },
    linkToThread(thread) {
      if (thread.module_id && this.getAssetId(thread)) {
        return {
          name: 'knowledge_thread',
          params: {
            moduleId: thread.module_id,
            threadId: thread.id,
          },
        };
      }
      return {
        name: 'profile_knowledge_thread',
        params: {
          threadId: thread.id,
        },
      };
    },

    getUserQuestions(fromPagination) {
      this.$logger.info('Getting user questions', { studentId: this.studentId }, true);
      this.$http.get(`/api/curriculum/knowledge/threads/user/${this.studentId}`, {
        params: {
          page: this.currentPage + 1,
          limit: this.maxItemsPerPage,
        },
      })
        .then(response => {
          this.$logger.info('Got user questions', { studentId: this.studentId });
          this.threads = response.data.threads;
          this.threadsTotal = response.data.total;
        })
        .catch(err => {
          this.$logger.autowarn('Error getting user questions', { studentId: this.studentId }, err);
          this.$ktoast.showError(err);
        })
        .then(() => {
          this.threadsReady = true;
          if (fromPagination) {
            this.$Loading.finish();
          }
        });
    },
  },
};
</script>
